<template>
	<v-card v-show="!isSubpageOpen" :class="cardClass()">
		<v-card-text class="flex-grow-0 flex-shrink-0">
			<div class="header">
				<div>
					<h2><span class="action-header-record">Start</span> a Recording Now</h2>
				</div>
			</div>
			<v-form>
				<CustomerAutoComplete
					v-model="selectedCustomer"
					:disabled="restoringInstructionDraft || resolvingCustomerPrefill"
					label="Contact (Optional)"
					density="compact"
					@update:modelValue="setCustomer" />
				<ProjectAutoComplete
					:model-value="newSelectedProject"
					:disabled="disableProjectField || restoringInstructionDraft"
					:entity="null"
					density="compact"
					label="Project (Optional)"
					@update:modelValue="updateProject" />
				<AddressLookupLink v-if="!disableProjectField && !restoringInstructionDraft" @openAddressLookup="openAddressLookup" />

				<!-- LSP-20406: tag the session as part of starting the action. -->
				<SessionTagPicker v-model="selectedSessionTags" />
				<SmartPackSelector
					v-model="scribePackSelection"
					ref="smartPackSelector"
					@update:model-value="scribePackSelectionTouched = true"
					@update:processing="updateScribePackProcessing"
					@update:user-input-pending="scribeUserInputPending = $event" />
				<v-alert
					v-if="scribeUserInputPending"
					type="info"
					variant="tonal"
					density="compact"
					class="mt-3 cursor-pointer"
					@click="$refs.smartPackSelector?.reopenDialog()">
					<div class="text-body-2">This Spark report requires additional input. Click here to provide the required details.</div>
				</v-alert>
				<ScheduledSparkNotice v-if="scribePackSelection" :delay-seconds="contentNotificationDelay" />

				<InstructionPicker
					v-model="instructionId"
					class="mt-2"
					:create-return-to="instructionCreateReturnTo"
					create-return-action="async-recording"
					:create-draft-key="instructionReturn.storageKey()"
					:disabled="restoringInstructionDraft || resolvingCustomerPrefill"
					:preselect-default="instructionPreselectAllowed"
					@preselect-settled="instructionPreselectAllowed = false"
					@create-start="saveInstructionCreateDraft" />
			</v-form>

			<div class="recording-disclaimer">
				Click “Start Recording” and a new tab will open with your camera and microphone ready to go.
			</div>
		</v-card-text>
		<v-card-actions class="d-flex justify-space-between pa-2 ga-2">
			<!--Close-->
			<v-btn
				style="flex: 1"
				size="x-large"
				rounded="xl"
				variant="outlined"
				elevation="0"
				@click.stop.prevent="close"
				@touchstart.stop.prevent="close()">
				Cancel
			</v-btn>

			<!--Start-->
			<v-btn
				:disabled="
					isLoading ||
					resolvingCustomerPrefill ||
					restoringInstructionDraft ||
					instructionDraftRestoreFailed ||
					(projectId && !newSelectedProject) ||
					!recordingName ||
					recordingName.trim() === '' ||
					scribePackProcessing ||
					scribeUserInputPending
				"
				elevation="0"
				style="flex: 1"
				size="x-large"
				rounded="xl"
				variant="flat"
				color="#ff460b"
				:loading="isLoading"
				@click="startInstantMeeting">
				Start Recording
			</v-btn>
		</v-card-actions>
	</v-card>
	<AddressLookup v-if="isAddressLookupOpen" @projectSelected="updateProject" @close="isAddressLookupOpen = false" />
</template>

<script>
	import api from "../api";
	import { auth } from "@/auth";
	import "../helpers/emoji";
	import constants from "../api/constants";
	import moment from "moment-timezone";
	import { getDateTokens } from "@/helpers/dateFormat";
	import { AsyncPreScreenBody, AsyncPreScreenHead, writePreScreen } from "../helpers/string";
	import DeviceUtility from "@/helpers/device";
	import { isProjectFlow, navigateAfterActionStarted, returnToProjectAfterActionStarted } from "@/helpers/projectFlowReturn";
	import CustomerAutoComplete from "@/components/formfields/CustomerAutoComplete.vue";
	import ProjectAutoComplete from "@/components/formfields/ProjectAutoComplete.vue";
	import SessionTagPicker from "@/components/tags/SessionTagPicker.vue";
	import { hasFreeTrialEnded } from "../helpers/hasFreeTrialEnded.js";
	import { closeActionDialog } from "@/helpers/closeActionDialogs";
	import { countCharacters, getMessageFromError, calculateTimeout, getTrimmedPhoneFromClipboard } from "@/helpers/utilities";
	import { getVueTelProps } from "@/helpers/phone";
	import AddressLookupLink from "./location/AddressLookupLink.vue";
	import SmartPackSelector from "./scribe/SmartPackSelector.vue";
	import InstructionPicker from "./InstructionPicker.vue";
	import ScheduledSparkNotice from "./scribe/ScheduledSparkNotice.vue";
	import AddressLookup from "./location/AddressLookup.vue";
	import { buildAdminAsyncLaunchUrl } from "@/helpers/sparkyAuthHandoff";
	import { useToast } from "vue-toastification";
	import {
		useInstructionCreateReturn,
		isValidScribePackSelectionDraft,
		normalizeScribePackSelectionDraft
	} from "@/composables/useInstructionCreateReturn";

	const DEFAULT_INSTRUCTION_DRAFT_KEY = "contact:start-recording:create-instruction-draft";

	export default {
		components: {
			CustomerAutoComplete,
			ProjectAutoComplete,
			SessionTagPicker,
			AddressLookup,
			AddressLookupLink,
			SmartPackSelector,
			ScheduledSparkNotice,
			InstructionPicker
		},
		emits: ["close-menu"],
		mixins: [closeActionDialog],
		setup(props) {
			const toast = useToast();
			const instructionReturn = useInstructionCreateReturn({
				defaultDraftKey: DEFAULT_INSTRUCTION_DRAFT_KEY,
				getDraftKey: () => props.instructionCreateDraftKey,
				label: "Start Recording"
			});
			return { toast, instructionReturn };
		},
		props: {
			customerId: String,
			actionText: String,
			actioningText: String,
			allowJoin: Boolean,
			buttonIcon: String,
			showHeader: Boolean,
			projectId: String,
			project: {
				type: Object,
				required: false
			},
			projectChecklistItemId: String,
			// LSP-16289: the freshly created instruction id handed back from the in-app editor.
			// It wins over any instructionId restored from the persisted draft.
			initialInstructionId: {
				type: String,
				default: null
			},
			// When present, the Guided Instructions picker navigates in the SAME tab to the editor
			// and returns to this path; null keeps the legacy new-tab behavior.
			instructionCreateReturnTo: {
				type: String,
				default: null
			},
			// sessionStorage slot used to persist/restore the in-progress dialog fields across the
			// same-tab editor detour.
			instructionCreateDraftKey: {
				type: String,
				default: null
			}
		},
		data() {
			return {
				isAddressLookupOpen: false,
				vueTelProps: getVueTelProps(),
				starting: false,
				validPhone: false,
				autoSetName: false,
				autoSetNameValue: "",
				isLoading: false,
				phone: "",
				name: "",
				email: "",
				recordingName: "",
				meetingUrl: "",
				actioningTextOverride: "",
				organization: {},
				conversationType: "async",
				autoStartRecordings: false,
				me: null,
				selectedCustomer: null,
				customerIdFallback: null,
				contactSelectionVersion: 0,
				projectSelectionVersion: 0,
				resolvingCustomerPrefill: Boolean(this.customerId),
				restoringInstructionDraft: this.instructionCreateDraftKey !== null || this.initialInstructionId !== null,
				instructionDraftRestoreFailed: false,
				instructionDraftRestoreFailures: { project: false, contact: false },
				newSelectedProject: null,
				// LSP-20406: tags applied to the session at creation.
				selectedSessionTags: [],
				projectSelectItems: [],
				placeholderRecordingName: "",
				disableProjectField: false,
				overridePopupUrl: "",
				scribePackSelection: null,
				scribePackSelectionTouched: false,
				scribePackProcessing: false,
				scribeUserInputPending: false,
				instructionId: null,
				// LSP-20840: preselecting the org default is allowed on a fresh open, or after the
				// LSP-16289 restore settles WITHOUT consuming a draft/returned id — a consumed draft
				// (even one whose instructionId is an explicit clear) always wins over the default.
				instructionPreselectAllowed: false
			};
		},
		computed: {
			contentNotificationDelay() {
				return this.me?.config?.contentNotificationDelaySeconds;
			},
			isSubpageOpen() {
				return this.isAddressLookupOpen;
			},
			dynamicActioningText() {
				if (this.actioningTextOverride) {
					return this.actioningTextOverride;
				}
				return this.starting ? this.actioningText : this.actionText;
			},
			dynamicName() {
				return this.nameOverride ?? this.name;
			},
			disablePhoneNumberEdit() {
				return !!this.meetingUrl;
			},
			allowReset() {
				return this.meetingUrl;
			}
		},
		async mounted() {
			const returnedInstruction =
				(this.$route.path || "").replace(/\/+$/, "").toLowerCase() === "/async-recording"
					? this.instructionReturn.consumeReturnQuery(this.$route, this.$router, "async-recording")
					: null;
			// order matters here
			this.reset(); // make sure this goes first
			if (returnedInstruction) this.restoringInstructionDraft = true;
			const contactSelectionVersion = this.contactSelectionVersion;
			if (this.customerId) this.customerIdFallback = this.customerId;
			this.scribePackSelectionTouched = false;
			const projectSelectionVersion = this.projectSelectionVersion;
			this.tryOpenAddressLookup();

			this.me = await api.getMe();
			// Must precede getConversationName(): it reads this.organization.dateFormat to format
			// the date baked into the (end-user-visible) recording name. (LSP-15050)
			this.organization = this.me.organization;
			this.recordingName = this.getConversationName();
			this.placeholderRecordingName = this.recordingName;

			if (this.project && projectSelectionVersion === this.projectSelectionVersion) {
				this.newSelectedProject = this.project;
			} else if (this.projectId) {
				const result = await api.getProject(this.projectId);
				if (projectSelectionVersion === this.projectSelectionVersion) {
					this.newSelectedProject = result;
				}
			}

			if (this.projectChecklistItemId || this.projectId || this.project) {
				this.disableProjectField = true;
			}

			this.autoStartRecordings = this.organization.autoStartRecordings;
			this.notificationTemplate =
				this.conversationType == "async" ? this.organization.notificationTemplateAsync : this.organization.notificationTemplate;
			this.defaultNotificationTemplate = this.notificationTemplate;

			if (this.customerId) {
				try {
					const result = await api.getCustomers({
						where: {
							id: this.customerId
						}
					});

					if (result.count > 0 && contactSelectionVersion === this.contactSelectionVersion) {
						this.setCustomer(result.rows[0]);
					}
				} catch {
					// Contact prefill is optional; keep the dialog usable when the lookup fails.
				} finally {
					this.resolvingCustomerPrefill = false;
				}
			} else {
				this.resolvingCustomerPrefill = false;
			}
			// check if there is a overridePopupUrl in the query params
			const params = new URLSearchParams(window.location.search);
			const overridePopupUrl = params.get("overridePopupUrl");
			if (overridePopupUrl) {
				this.overridePopupUrl = overridePopupUrl;
			}

			// LSP-16289: only restore when a return-handoff signal is present. The null check (not
			// truthiness) is intentional: empty-string initialInstructionId is still an explicit
			// return signal; only null (default) means "no handoff, fresh open."
			if (returnedInstruction || this.restoringInstructionDraft) {
				try {
					await this.restoreInstructionCreateDraft();
					if (this.instructionDraftRestoreFailed) {
						this.toast.error("Unable to restore a saved selection. Choose the contact or project again before starting.", {
							timeout: calculateTimeout(
								"Unable to restore a saved selection. Choose the contact or project again before starting."
							)
						});
					}
				} finally {
					this.restoringInstructionDraft = false;
				}
			} else {
				// LSP-20840: fresh open with no handoff context at all — the default may preselect.
				this.instructionPreselectAllowed = true;
			}
		},
		methods: {
			updateScribePackProcessing(processing) {
				this.scribePackProcessing = processing;
				if (processing) this.scribePackSelectionTouched = true;
			},
			tryOpenAddressLookup() {
				const params = new URLSearchParams(window.location.search);
				const goTo = params.get("goTo");
				if (goTo === "address") {
					this.openAddressLookup();
				}
			},
			openAddressLookup() {
				this.isAddressLookupOpen = true;
			},
			async updateProject(project) {
				this.isAddressLookupOpen = false;
				this.projectSelectionVersion += 1;
				this.newSelectedProject = project;
				this.clearFailedInstructionDraft("project");
			},
			cardClass() {
				return `pa-1 conversation-card ${DeviceUtility.isMobile ? "conversation-bottom" : ""} ${this.conversationType}`;
			},
			startButtonDisabled() {
				// Project-wide rule: action-dialog disabled guards check BOTH `scribePackProcessing`
				// (Spark report generation in flight) AND `scribeUserInputPending` (Spark required
				// user input not yet supplied). Inline disabled binding on the template button row
				// already checks both; this computed must match so the value is consistent for any
				// other consumer that reads it.
				return (
					!this.validPhone ||
					this.isLoading ||
					this.starting ||
					this.resolvingCustomerPrefill ||
					this.restoringInstructionDraft ||
					this.instructionDraftRestoreFailed ||
					!!this.actioningTextOverride ||
					(this.projectId && !this.newSelectedProject) ||
					this.scribePackProcessing ||
					this.scribeUserInputPending
				);
			},
			reset() {
				this.$refs.smartPackSelector?.reset?.();
				this.scribePackSelectionTouched = false;
				this.isAddressLookupOpen = false;
				this.meetingUrl = "";
				this.phone = "";
				this.name = "";
				this.email = "";
				this.autoSetName = "";
				this.autoSetNameValue = "";
				this.contactSelectionVersion += 1;
				this.projectSelectionVersion += 1;
				this.selectedCustomer = null;
				this.customerIdFallback = null;
				this.scribePackSelection = null;
				this.selectedSessionTags = [];
				this.instructionId = null;
				this.instructionDraftRestoreFailed = false;
				this.instructionDraftRestoreFailures = { project: false, contact: false };
				this.scribePackProcessing = false;
				this.scribeUserInputPending = false;
				this.notificationTemplate =
					this.conversationType == "async" ? this.organization.notificationTemplateAsync : this.organization.notificationTemplate;
				setTimeout(() => {
					try {
						this.$refs.recordingNameInput.$refs.input.focus();
					} catch {
						// The input ref may be absent in non-dialog render states.
					}
				}, 500);
			},
			updateRecordingName() {
				if (!this.recordingName || this.recordingName.length == 0) {
					this.recordingName = this.getConversationName();
				}
			},
			// TODO: Consolidate this into a helper or single Action dialog component
			getTeamMemberName() {
				let name = null;

				if (this.me?.user.firstname) {
					name = this.me?.user.firstname;
				}

				if (this.me?.user.lastname) {
					name = `${name} ${this.me?.user.lastname}`;
				}

				if (!name) {
					name = this.me?.user.email;
				}

				return name ?? "-";
			},
			getConversationName() {
				const teamMember = this.getTeamMemberName();
				const now = moment().format(getDateTokens(this.organization?.dateFormat).dateShortMonth);
				const conversationName = `Recording from ${teamMember} on ${now}`;
				return conversationName;
			},
			countCharacters(value) {
				return countCharacters(value);
			},
			pasteEvent(event) {
				this.phone = getTrimmedPhoneFromClipboard(event);
				// If a user pastes into the vue-tel-input, force it to reformat itself.
				this.$nextTick(function () {
					try {
						this.$refs.phoneInput.onInput();
					} catch {
						// The phone input ref may not exist when the field is not rendered.
					}
				});
			},
			// Fires when the correctness of the vue-tel-input's phone number changes (from true to false or vice-versa) and when the component is mounted.
			validate(args) {
				this.validPhone = args.valid;
			},
			// Fires when the vue-tel-input's input changes.
			async matchPhone(number, phoneObject) {
				if (!this.validPhone || !phoneObject) {
					// bail on invalid numbers
					return;
				}

				// Make sure that even if the user changes the formatting of their phone number, we always internally
				// update it to formatted version. This way, when the phone number gets saved to the database, it is
				// always properly formatted.
				this.phone = phoneObject.formatted;

				if (!number && this.autoSetName) {
					this.name = "";
				}

				const result = await api.getCustomers({
					where: {
						"$PhoneNumbers.phoneNumber$": {
							$like: `%${number}%`
						}
					}
				});

				if (result.count > 0) {
					this.setCustomer(result.rows[0]);
				}
			},
			setCustomer(customer) {
				this.contactSelectionVersion += 1;
				this.selectedCustomer = customer;
				this.customerIdFallback = null;
				this.clearFailedInstructionDraft("contact");
				if (!customer) {
					this.name = "";
					this.email = "";
					this.autoSetName = false;
					this.autoSetNameValue = "";
					return;
				}
				this.name = customer.firstname + " " + customer.lastname;
				this.autoSetName = true;
				this.autoSetNameValue = this.name;

				if (customer.EmailAddresses?.length > 0) {
					this.email = customer.EmailAddresses[0].emailAddress;
				}
			},
			clearFailedInstructionDraft(selection) {
				if (!this.restoringInstructionDraft && this.instructionDraftRestoreFailed) {
					this.instructionDraftRestoreFailures[selection] = false;
					this.instructionDraftRestoreFailed = Object.values(this.instructionDraftRestoreFailures).some(Boolean);
					if (!this.instructionDraftRestoreFailed) {
						this.instructionReturn.clearDraft();
					}
				}
			},
			async startInstantMeeting() {
				if (
					this.scribeUserInputPending ||
					this.isLoading ||
					this.resolvingCustomerPrefill ||
					this.restoringInstructionDraft ||
					this.instructionDraftRestoreFailed
				) {
					return;
				}
				// Safari Fix - Pre-open window before async.
				if (hasFreeTrialEnded(this.organization, this.me?.organizationSubscription)) {
					this.$root.$freeTrialEndedDialog.open(this.organization);
				} else {
					// Acquire the click guard before opening the popup or awaiting the optional
					// override URL delay. Two clicks in the same browser task must still create once.
					this.isLoading = true;
					// ls-heroku-ci (HIGH): ONE identity for the whole action, captured before ANY await, the same
					// principle ActionUploadPhoto.save() applies. The contact back-fill below runs after getMe (and
					// after the popup/pre-screen waits) and read the project and contact LIVE, so a contact or project
					// switched while those awaits were in flight wrote the WRONG contact into the WRONG project's
					// Customers list. That is a persistent api.updateProject write, not a display glitch.
					//
					// Used for the create request too, deliberately. Snapshotting only the back-fill would leave the
					// request reading live and the two would then disagree about which project the action was for --
					// the half-applied-snapshot mistake this PR already had to correct once in ActionUploadPhoto,
					// where it wrote one customer onto the project and a different one onto the conversation.
					//
					const actionProject = this.newSelectedProject;
					const actionContact = this.selectedCustomer ? { ...this.selectedCustomer } : null;
					const actionCustomerIdFallback = this.customerIdFallback;
					const actionScribePackSelection = this.scribePackSelection;
					// LSP-20406: captured with the other send inputs so a selection edit during the awaited
					// create cannot change which tags the request carries.
					const actionSessionTags = [...this.selectedSessionTags];
					let win = null;
					// LSP-15388 review (CRITICAL): once the popup is pointed at the recording url the
					// user's recording is LIVE. Nothing after that may reach the catch below, which
					// closes the popup — that would destroy a recording that started successfully.
					let recordingStarted = false;
					// SINGLE path for tearing the popup down, so a future close cannot be added without
					// inheriting the guard (review follow-up: ActionPhoto had a second, unguarded close).
					const closePopupUnlessRecordingStarted = (phase) => {
						if (recordingStarted) {
							console.warn("ActionStartRecording: refusing to close the popup — the recording is live", {
								tags: { method: "startInstantMeeting", file: "ActionStartRecording", phase },
								extra: { conversationType: this.conversationType }
							});
							return;
						}
						if (!win) {
							return;
						}
						try {
							win.close();
						} catch (closeError) {
							console.error("Unable to close popup", closeError);
						}
					};
					let popupOpened = false;
					let recordingUrl = null;
					// LSP-15319: gate on the PWA itself, not on Android. isPwaInstalled is
					// matchMedia("(display-mode: standalone)"), which an iOS home-screen app reports
					// too — so the old `isAndroid &&` conjunct sent installed iOS PWAs down the popup
					// path, where there are no browser tabs to open into. That either handed the
					// recording off to Safari or returned null and raised a popup-blocker toast the
					// user could do nothing about. Every installed PWA now takes the current-tab
					// fallback, which is the branch that already worked on Android.
					const installedPwa = DeviceUtility.isPwaInstalled;

					try {
						if (!installedPwa) {
							win = window.open(this.overridePopupUrl || "", "_blank");
							if (!win) {
								this.toast.error(
									"Unable to open a new window. Please check your popup blocker settings or close some tabs and try again.",
									{
										timeout: calculateTimeout(
											"Unable to open a new window. Please check your popup blocker settings or close some tabs and try again."
										)
									}
								);
								return;
							}
							if (this.overridePopupUrl) {
								//sleep for 1 second
								await new Promise((resolve) => setTimeout(resolve, 1000));
							}
						}

						try {
							// accessing the popup window can throw an exception in certain browsers in some unknown conditions
							// this has been observed in Safari
							if (win && win.document && win.document.body) {
								writePreScreen(win, AsyncPreScreenHead, AsyncPreScreenBody(window.location.origin));
								popupOpened = true;
							}
						} catch (error) {
							// log and continue as we have fallbacks in place
							console.warn("Unable to set popup content. Will use window redirect instead.", error);
						}

						const me = await api.getMe();

						if (actionProject?.id && actionContact?.id) {
							// ProjectAutoComplete normally supplies /projects/lite rows, which omit
							// Customers. Fetch the complete association set before using the replacing
							// update endpoint, or adding one contact would delete every omitted link.
							let projectCustomers = actionProject.Customers;
							let projectContactLookupFailed = false;
							if (!Array.isArray(projectCustomers)) {
								try {
									const completeProject = await api.getProject(actionProject.id);
									projectCustomers = completeProject?.Customers;
								} catch (error) {
									projectContactLookupFailed = true;
									console.warn("Unable to load project contacts before recording; continuing without back-fill", {
										projectId: actionProject.id,
										error
									});
								}
							}
							if (Array.isArray(projectCustomers) && !projectCustomers.some((contact) => contact.id === actionContact.id)) {
								await api.updateProject(actionProject.id, {
									Customers: [...projectCustomers, actionContact]
								});
							} else if (!Array.isArray(projectCustomers) && !projectContactLookupFailed) {
								console.warn("Unable to back-fill the recording contact without a complete project contact list", {
									projectId: actionProject.id
								});
							}
						}
						// Captured BEFORE the awaited createConversation, mirroring ActionUploadPhoto's
						// projectIdForReturn: read live at the return sites below, a project change during this
						// await sends the user to a project that does not own the new conversation (PR #3891 review).
						// Exactly what the request below carries -- `?? ""` rather than `?? this.projectId`,
						// so a create with no project cannot be redirected to one anyway (break-it round 1).
						const projectIdForReturn = actionProject?.id ?? "";
						const resp = await api.createConversation({
							forcedUserId: me.user.id,
							customerId: actionContact?.id ?? actionCustomerIdFallback ?? undefined,
							name: this.recordingName ?? null,
							type: constants.CONVERSATION_TYPE.ASYNC,
							initiationType: constants.INITIATION_TYPE.ADMIN,
							autoStartRecordings: true,
							redirectToHostEditor: true,
							projectId: actionProject?.id,
							projectChecklistItemId: this.projectChecklistItemId,
							scribePackId: actionScribePackSelection?.pack?.id ?? undefined,
							scribePackName: actionScribePackSelection?.pack?.name ?? undefined,
							scribePackTaskIds: actionScribePackSelection?.taskIds ?? undefined,
							scribePackUserInputData: actionScribePackSelection?.userInputData ?? undefined,
							instructionId: this.instructionId ?? undefined,
							// LSP-20406: attach the picked tags atomically at creation (server upsertRelatedLabels).
							Labels: actionSessionTags.length ? actionSessionTags : undefined
						});

						recordingUrl = await this.appendAdminAsyncSparkyAuthToken(resp.url);

						if (popupOpened) {
							win.location = recordingUrl;
							// POINT OF NO RETURN — the recording is now live in the popup.
							recordingStarted = true;
							// Everything below is navigation/notification only. It is locally try/caught so a throw —
							// most plausibly a parent's synchronous close-menu handler, or a routing failure — cannot
							// propagate to the outer catch, which closes the popup the user is actively recording into.
							// Mirrors the guard ActionVideo.join already applies at its own popup hand-off.
							try {
								if (DeviceUtility.isMobile) {
									// LSP-15388: the mobile FAB routes here as a standalone /async-recording page,
									// so the project is unmounted and close-menu has no listener. The sibling
									// branches below already return the user somewhere ($router.go(-1) / the new
									// conversation) — the project flow was the only one that stranded them.
									if (isProjectFlow(this.$route, "ActionStartRecording")) {
										// ls-heroku-ci: the emit gets its OWN try, matching ActionVideo and ActionUploadPhoto. Sharing
										// one try with the navigate call below let a throwing close-menu listener skip the navigation
										// entirely and land in the outer catch -- the user stays put with only a warning. Not
										// exploitable with today's listener wiring, but that is a coincidence of which components
										// happen to be mounted, not a guarantee.
										try {
											this.$emit("close-menu", resp.conversation.id);
										} catch (error) {
											console.warn(
												"ActionStartRecording.start: a close-menu consumer failed, continuing to navigate",
												{
													tags: { method: "start", file: "ActionStartRecording" },
													extra: { conversationId: resp.conversation.id, error }
												}
											);
										}
										returnToProjectAfterActionStarted({
											route: this.$route,
											router: this.$router,
											projectId: projectIdForReturn,
											componentName: "ActionStartRecording"
										});
									} else if (this.$route.query.from !== "homepage") {
										this.$router.go(-1);
									} else {
										// ls-heroku-ci (HIGH): through the shared helper, like the project branch above and like
										// ActionPhoto's equivalent. The enclosing try/catch stops a SYNCHRONOUS throw, but
										// vue-router RESOLVES with a NavigationFailure for an aborted/cancelled/duplicated
										// navigation and can reject from a guard — neither reaches a catch, so a recording that
										// went live and then failed to navigate left no trace at all.
										navigateAfterActionStarted({
											router: this.$router,
											target: {
												name: "conversation",
												params: { id: resp.conversation.id },
												query: { from: "homepage" }
											},
											componentName: "ActionStartRecording",
											context: { conversationId: resp.conversation.id },
											destination: "to the conversation"
										});
									}
								} else {
									// Desktop: the dialog sits over a mounted project page, so close-menu is the
									// whole contract and returnToProjectAfterActionStarted correctly no-ops.
									if (isProjectFlow(this.$route, "ActionStartRecording")) {
										// ls-heroku-ci: the emit gets its OWN try, matching ActionVideo and ActionUploadPhoto. Sharing
										// one try with the navigate call below let a throwing close-menu listener skip the navigation
										// entirely and land in the outer catch -- the user stays put with only a warning. Not
										// exploitable with today's listener wiring, but that is a coincidence of which components
										// happen to be mounted, not a guarantee.
										try {
											this.$emit("close-menu", resp.conversation.id);
										} catch (error) {
											console.warn(
												"ActionStartRecording.start: a close-menu consumer failed, continuing to navigate",
												{
													tags: { method: "start", file: "ActionStartRecording" },
													extra: { conversationId: resp.conversation.id, error }
												}
											);
										}
										returnToProjectAfterActionStarted({
											route: this.$route,
											router: this.$router,
											projectId: projectIdForReturn,
											componentName: "ActionStartRecording"
										});
									} else {
										// ls-heroku-ci (HIGH): through the shared helper, like the project branch above and like
										// ActionPhoto's equivalent. The enclosing try/catch stops a SYNCHRONOUS throw, but
										// vue-router RESOLVES with a NavigationFailure for an aborted/cancelled/duplicated
										// navigation and can reject from a guard — neither reaches a catch, so a recording that
										// went live and then failed to navigate left no trace at all.
										navigateAfterActionStarted({
											router: this.$router,
											target: { name: "conversation", params: { id: resp.conversation.id } },
											componentName: "ActionStartRecording",
											context: { conversationId: resp.conversation.id },
											destination: "to the conversation"
										});
									}
								}
							} catch (navigationError) {
								console.warn("ActionStartRecording: post-recording hand-off failed; the recording itself is unaffected", {
									tags: { method: "startInstantMeeting", file: "ActionStartRecording", phase: "post-recording-handoff" },
									extra: { conversationId: resp.conversation?.id, error: navigationError }
								});
							}
						} else {
							if (installedPwa) {
								console.log("Installed PWA. Redirecting current tab", {
									level: "info",
									isAndroid: DeviceUtility.isAndroid,
									isIOS: DeviceUtility.isIOS
								});
							} else {
								console.error("Unable to open popup. Redirecting in current tab.");
							}
							window.location = recordingUrl;
						}
					} catch (e) {
						const message = getMessageFromError(e);
						this.toast.error(message, {
							timeout: calculateTimeout(message)
						});

						console.error(e, {
							tags: {
								method: "startInstantMeeting",
								file: "ActionStartRecording"
							},
							extra: {
								type: this.conversationType
							}
						});

						closePopupUnlessRecordingStarted("post-recording-error");

						// If all else fails. Also gated: once the recording is live in the popup, navigating
						// THIS tab to the recording url starts a second session rather than recovering the
						// first.
						if (recordingUrl && !recordingStarted) {
							console.error("Unable to start recording. Redirecting in current tab.");
							window.location = recordingUrl;
							return;
						}
					} finally {
						this.isLoading = false;
					}
				}
			},
			// LSP-21256: also carries the dashboard launch surface to the recorder (buildAdminAsyncLaunchUrl).
			async appendAdminAsyncSparkyAuthToken(url) {
				return buildAdminAsyncLaunchUrl(url, auth);
			},
			// Thin accessor over the composable so existing callers/tests can read the resolved key.
			instructionDraftStorageKey() {
				return this.instructionReturn.storageKey();
			},
			// Persist the in-progress dialog fields before the same-tab navigation to the editor.
			// Only the minimal serializable shape of the Spark selection is kept (the same fields
			// startInstantMeeting() reads), so a non-serializable selection cannot break JSON.stringify.
			saveInstructionCreateDraft() {
				this.instructionReturn.persistDraft({
					phone: this.phone,
					recordingName: this.recordingName,
					selectedCustomerId: this.selectedCustomer?.id ?? null,
					selectedProjectId: this.newSelectedProject?.id ?? null,
					scribePackSelection: this.scribePackSelection ? normalizeScribePackSelectionDraft(this.scribePackSelection) : null,
					scribeUserInputPending: this.scribeUserInputPending,
					instructionId: this.instructionId
				});
			},
			// Restore the draft saved by saveInstructionCreateDraft(). A returned `initialInstructionId`
			// takes precedence over the draft's (now stale) instructionId.
			// The mounted() hook gates this call on return-handoff signals — callers that invoke this
			// method directly are responsible for ensuring the context is correct.
			async restoreInstructionCreateDraft() {
				const draft = this.instructionReturn.loadRawDraft();
				// LSP-20840: nothing to restore (no draft, no returned id) → the default may preselect.
				this.instructionPreselectAllowed = !draft && this.initialInstructionId === null;
				this.instructionDraftRestoreFailed = false;
				this.instructionDraftRestoreFailures = { project: false, contact: false };
				const contactSelectionVersion = this.contactSelectionVersion;
				const projectSelectionVersion = this.projectSelectionVersion;
				if (draft) {
					if (typeof draft.phone === "string") {
						this.phone = draft.phone;
					}
					if (typeof draft.recordingName === "string") {
						this.recordingName = draft.recordingName;
					}
					if (
						!this.scribePackSelectionTouched &&
						Object.prototype.hasOwnProperty.call(draft, "scribePackSelection") &&
						(draft.scribePackSelection === null || isValidScribePackSelectionDraft(draft.scribePackSelection))
					) {
						this.scribePackSelection =
							draft.scribePackSelection === null ? null : normalizeScribePackSelectionDraft(draft.scribePackSelection);
						if (typeof draft.scribeUserInputPending === "boolean") {
							this.scribeUserInputPending = draft.scribeUserInputPending;
						}
					}
					// A route/project prop owns the project when the picker is locked. A stale
					// detour draft must not replace it or create a failure the user cannot edit.
					if (typeof draft.selectedProjectId === "string" && draft.selectedProjectId && !this.disableProjectField) {
						try {
							const restoredProject = await api.getProject(draft.selectedProjectId);
							if (restoredProject?.id !== draft.selectedProjectId) {
								this.instructionDraftRestoreFailures.project = true;
								this.instructionDraftRestoreFailed = true;
								console.warn("Start Recording draft: selected project no longer resolves on restore", {
									selectedProjectId: draft.selectedProjectId
								});
							} else if (projectSelectionVersion === this.projectSelectionVersion) {
								this.newSelectedProject = restoredProject;
							}
						} catch (error) {
							this.instructionDraftRestoreFailures.project = true;
							this.instructionDraftRestoreFailed = true;
							console.warn("Unable to restore the selected project from the Start Recording draft", error);
						}
					}
					if (typeof draft.selectedCustomerId === "string" && draft.selectedCustomerId) {
						try {
							const result = await api.getCustomers({ where: { id: draft.selectedCustomerId } });
							if (result.count > 0) {
								if (contactSelectionVersion === this.contactSelectionVersion) {
									this.setCustomer(result.rows[0]);
								}
							} else {
								this.instructionDraftRestoreFailures.contact = true;
								this.instructionDraftRestoreFailed = true;
								console.warn("Start Recording draft: selected contact no longer resolves on restore", {
									selectedCustomerId: draft.selectedCustomerId
								});
							}
						} catch (error) {
							this.instructionDraftRestoreFailures.contact = true;
							this.instructionDraftRestoreFailed = true;
							console.warn("Unable to restore the selected contact from the Start Recording draft", error);
						}
					} else if (draft.selectedCustomerId === null && contactSelectionVersion === this.contactSelectionVersion) {
						this.setCustomer(null);
					}
				}
				this.instructionId = this.instructionReturn.resolveInstructionId(
					draft?.instructionId,
					this.instructionReturn.returnedInstructionId(this.initialInstructionId),
					this.instructionId
				);
				if (!this.instructionDraftRestoreFailed) {
					this.instructionReturn.clearDraft();
				}
			}
		}
	};
</script>

<style lang="scss" scoped>
	.recording-disclaimer {
		color: #5e5c6e;
		font-size: 0.9em;
		text-align: center;
		padding: 24px;
	}
	.conversation-card {
		border-radius: 12px;
		min-height: 400px;
		display: flex;
		flex-direction: column;
		justify-content: start;
	}

	.conversation-card .header {
		display: flex;
		margin-bottom: 28px;
		margin-top: 12px;
		color: #000;
	}

	.conversation-card .header.schedule {
		justify-content: space-between;
	}

	.conversation-card .header.schedule .v-btn {
		background-color: #fff;
		border: 1px solid #03011d;
		color: #03011d;
		font-weight: 600;
		padding: 0 6px;
	}

	.conversation-card .header.schedule .v-btn.open {
		background-color: #dff0ff;
	}

	.conversation-card textarea {
		color: #212121;
		font-size: 16px;
	}

	.conversation-card h2,
	.conversation-card i {
		font-size: 24px;
		margin-right: 5px;
	}

	.conversation-card .v-card__actions {
		padding: 16px;
		display: flex;
		justify-content: space-evenly;
	}

	.conversation-card .v-card__actions > button.back {
		width: 100%;
	}

	.conversation-card .auto-message {
		color: #b3b3b3;
		font-size: 16px;
	}

	.conversation-card .record-switch {
		margin-left: 5px;
		margin-top: 0px;
	}

	.conversation-card .contact-name.reset {
		padding-top: 0px;
	}
</style>
